import { getStoredToken } from '../utils/auth';

const API_BASE_URL =
  process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api';

class ApiService {
  constructor() {
    this.eventSource = null;
    this.listeners = new Set();
  }

  authHeaders() {
    const token = getStoredToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async request(path, options = {}) {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      headers: {
        'Content-Type': 'application/json',
        ...this.authHeaders(),
        ...options.headers,
      },
      ...options,
    });
    let result;
    try {
      result = await response.json();
    } catch {
      throw new Error(
        response.ok ? 'Invalid server response' : `Server error (${response.status})`,
      );
    }
    if (!response.ok || !result.success) {
      throw new Error(result.error || `Request failed (${response.status})`);
    }
    return result.data;
  }

  async login(username, password, role) {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, role }),
    });
    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Giriş başarısız');
    }
    return result.data;
  }

  fetchCalls() {
    return this.request('/calls');
  }

  fetchStats() {
    return this.request('/stats');
  }

  fetchSettings() {
    return this.request('/settings');
  }

  saveSettings(settings) {
    return fetch(`${API_BASE_URL}/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
      body: JSON.stringify(settings),
    }).then((r) => r.json()).then((res) => {
      if (!res.success) throw new Error(res.error);
      return res.data;
    });
  }

  fetchAppointments() {
    return this.request('/appointments');
  }

  fetchSlots(date) {
    return this.request(`/appointments/slots?date=${date}`);
  }

  createAppointment(payload) {
    return this.request('/appointments/direct', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  updateAppointment(id, patch) {
    return this.request(`/appointments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  deleteAppointment(id) {
    return this.request(`/appointments/${id}`, { method: 'DELETE' });
  }

  fetchCampaigns() {
    return this.request('/campaigns');
  }

  createCampaign(payload) {
    return this.request('/campaigns', { method: 'POST', body: JSON.stringify(payload) });
  }

  updateCampaign(id, patch) {
    return this.request(`/campaigns/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
  }

  deleteCampaign(id) {
    return this.request(`/campaigns/${id}`, { method: 'DELETE' });
  }

  async fetchCampaignDocuments() {
    const response = await fetch(`${API_BASE_URL}/campaign-documents`, {
      headers: { ...this.authHeaders() },
    });
    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error || `Request failed (${response.status})`);
    }
    return {
      documents: result.data || [],
      pineconeConfigured: result.pineconeConfigured,
      assistantName: result.assistantName,
    };
  }

  async uploadCampaignDocument(file) {
    const form = new FormData();
    form.append('file', file);
    const response = await fetch(`${API_BASE_URL}/campaign-documents`, {
      method: 'POST',
      headers: { ...this.authHeaders() },
      body: form,
    });
    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error || `Upload failed (${response.status})`);
    }
    return result.data;
  }

  refreshCampaignDocument(id) {
    return this.request(`/campaign-documents/${id}/refresh`, { method: 'POST' });
  }

  deleteCampaignDocument(id) {
    return this.request(`/campaign-documents/${id}`, { method: 'DELETE' });
  }

  fetchSocialConversations() {
    return this.request('/social/conversations');
  }

  fetchSocialMessages(conversationId) {
    return this.request(`/social/conversations/${conversationId}/messages`);
  }

  replySocialConversation(conversationId, text) {
    return this.request(`/social/conversations/${conversationId}/reply`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  takeoverSocialConversation(conversationId) {
    return this.request(`/social/conversations/${conversationId}/takeover`, { method: 'POST' });
  }

  releaseSocialConversation(conversationId) {
    return this.request(`/social/conversations/${conversationId}/release`, { method: 'POST' });
  }

  fetchSocialLeads() {
    return this.request('/social/leads');
  }

  fetchConversationSummaries({ externalUserId, platform } = {}) {
    const params = new URLSearchParams();
    if (externalUserId) params.set('externalUserId', externalUserId);
    if (platform && platform !== 'all') params.set('platform', platform);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/social/summaries${query}`);
  }

  fetchSummaryStats({ range = 'all', platform = 'all' } = {}) {
    const params = new URLSearchParams();
    if (range) params.set('range', range);
    if (platform) params.set('platform', platform);
    return this.request(`/social/summary-stats?${params.toString()}`);
  }

  testWebhook() {
    return fetch(`${API_BASE_URL}/test-webhook`, { method: 'POST' }).then((r) => r.json());
  }

  logWidgetSession(payload) {
    return fetch(`${API_BASE_URL}/calls/from-widget`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then((r) => r.json());
  }

  startRealTimeUpdates() {
    if (this.eventSource) return;
    this.eventSource = new EventSource(`${API_BASE_URL}/events`);
    this.eventSource.onmessage = (event) => {
      try {
        this.notifyListeners(JSON.parse(event.data));
      } catch (e) {
        console.error('SSE parse error', e);
      }
    };
    this.eventSource.onerror = () => {
      setTimeout(() => {
        if (this.eventSource?.readyState === EventSource.CLOSED) {
          this.stopRealTimeUpdates();
          this.startRealTimeUpdates();
        }
      }, 5000);
    };
  }

  stopRealTimeUpdates() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  addUpdateListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notifyListeners(data) {
    this.listeners.forEach((cb) => {
      try {
        cb(data);
      } catch (e) {
        console.error(e);
      }
    });
  }

  async checkHealth() {
    const response = await fetch(`${API_BASE_URL}/health`);
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Health check failed');
    }
    return result;
  }

  async isHealthy() {
    try {
      await this.checkHealth();
      return true;
    } catch {
      return false;
    }
  }

  fetchHealth() {
    return this.checkHealth();
  }
}

const apiService = new ApiService();
export default apiService;
